'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Slider,
  FormGroup,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useSettingsStore } from '@/stores/SettingsStore';
import { useTranslations } from 'next-intl';
import CustomMuiScrollBar from '../ui/ScrollBar';

export const SettingsDialog: React.FC = () => {
  const t = useTranslations('SettingsDialog');
  const {
    isSettingsOpen,
    closeSettings,
    workDuration,
    shortBreakDuration,
    longBreakDuration,
    longBreakInterval,
    soundEnabled,
    notificationEnabled,
    saveSettings,
  } = useSettingsStore();

  const [tempSettings, setTempSettings] = useState({
    workDuration,
    shortBreakDuration,
    longBreakDuration,
    longBreakInterval,
    soundEnabled,
    notificationEnabled,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('info');

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (isSettingsOpen) {
      setTempSettings({
        workDuration,
        shortBreakDuration,
        longBreakDuration,
        longBreakInterval,
        soundEnabled,
        notificationEnabled,
      });
    }
  }, [isSettingsOpen, workDuration, shortBreakDuration, longBreakDuration, longBreakInterval, soundEnabled, notificationEnabled]);

  const handleSave = () => {
    const hasChanges =
      tempSettings.workDuration !== workDuration ||
      tempSettings.shortBreakDuration !== shortBreakDuration ||
      tempSettings.longBreakDuration !== longBreakDuration ||
      tempSettings.longBreakInterval !== longBreakInterval ||
      tempSettings.soundEnabled !== soundEnabled ||
      tempSettings.notificationEnabled !== notificationEnabled;

    if (hasChanges) {
      saveSettings(tempSettings);
    } else {
      closeSettings();
    }
  };

  return (
    <Dialog
      open={isSettingsOpen}
      onClose={closeSettings}
      maxWidth="sm"
      fullWidth
      sx={{
        ".MuiDialog-paper": {
          borderRadius: 6,
          backgroundColor: 'var(--color-card)',
          color: 'var(--color-foreground)'
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div">
          {t('settings')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{
        maxHeight: '60vh',
        overflowY: 'auto',
        backgroundColor: 'var(--color-card)',
        color: 'var(--color-foreground)',
        ...CustomMuiScrollBar()
      }}>
        <Box sx={{ py: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t('pomodoroDurationSettings')}
          </Typography>

          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Box>
              <Typography variant="body2" gutterBottom>
                {t('workDuration')}
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={tempSettings.workDuration}
                  onChange={(_, value) => setTempSettings(prev => ({ ...prev, workDuration: value as number }))}
                  min={0.5}
                  max={60}
                  step={0.5}
                  marks={[{ value: 15, label: '15' }, { value: 25, label: '25' }, { value: 45, label: '45' }]}
                  valueLabelDisplay="auto"
                  sx={{
                    color: 'var(--color-primary)',
                    '& .MuiSlider-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-background)'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" gutterBottom>
                {t('shortBreakDuration')}
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={tempSettings.shortBreakDuration}
                  onChange={(_, value) => setTempSettings(prev => ({ ...prev, shortBreakDuration: value as number }))}
                  min={0.5}
                  max={15}
                  step={0.5}
                  marks={[{ value: 5, label: '5' }, { value: 10, label: '10' }]}
                  valueLabelDisplay="auto"
                  sx={{
                    color: 'var(--color-primary)',
                    '& .MuiSlider-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-background)'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" gutterBottom>
                {t('longBreakDuration')}
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={tempSettings.longBreakDuration}
                  onChange={(_, value) => setTempSettings(prev => ({ ...prev, longBreakDuration: value as number }))}
                  min={10}
                  max={30}
                  step={0.5}
                  marks={[{ value: 15, label: '15' }, { value: 20, label: '20' }, { value: 25, label: '25' }]}
                  valueLabelDisplay="auto"
                  sx={{
                    color: 'var(--color-primary)',
                    '& .MuiSlider-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-background)'
                    }
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" gutterBottom>
                {t('longBreakInterval')}
              </Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={tempSettings.longBreakInterval}
                  onChange={(_, value) => setTempSettings(prev => ({ ...prev, longBreakInterval: value as number }))}
                  min={2}
                  max={8}
                  step={1}
                  marks={[{ value: 2, label: '2' }, { value: 4, label: '4' }, { value: 6, label: '6' }]}
                  valueLabelDisplay="auto"
                  sx={{
                    color: 'var(--color-primary)',
                    '& .MuiSlider-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-markLabel': {
                      color: 'var(--color-muted-foreground)'
                    },
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-background)'
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            {t('soundAndNotificationSettings')}
          </Typography>

          <FormGroup row sx={{ gap: 8 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.soundEnabled}
                  onChange={(_, checked) => setTempSettings(prev => ({ ...prev, soundEnabled: checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--color-primary)'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    }
                  }}
                />
              }
              label={t('soundReminder')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={tempSettings.notificationEnabled}
                  onChange={async (_, checked) => {
                    if (checked) {
                      // User is trying to enable notifications
                      if (!('Notification' in window)) {
                        setSnackbarMessage(t('notificationNotSupported'));
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                        return;
                      }

                      if (Notification.permission === 'denied') {
                        setSnackbarMessage(t('notificationPermissionDenied'));
                        setSnackbarSeverity('warning');
                        setSnackbarOpen(true);
                        // Do not change the switch state if permission is denied
                        return;
                      }

                      if (Notification.permission === 'default') {
                        const permission = await Notification.requestPermission();
                        if (permission !== 'granted') {
                          // If permission is not granted, do not enable the switch
                          setSnackbarMessage(t('notificationPermissionNotGranted'));
                          setSnackbarSeverity('info');
                          setSnackbarOpen(true);
                          return;
                        }
                      }
                    }
                    // Only update state if permission is granted or if disabling
                    setTempSettings(prev => ({ ...prev, notificationEnabled: checked }));
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--color-primary)'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--color-primary)'
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: 'var(--color-muted-foreground)'
                    }
                  }}
                />
              }
              label={t('browserNotification')}
            />
          </FormGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        py: 2,
        justifyContent: 'center',
        gap: 2,
        backgroundColor: 'var(--color-card)',
      }}>
        <IconButton
          onClick={handleSave}
          color="primary"
          sx={{
            bgcolor: 'var(--color-primary)',
            color: 'var(--color-background)',
            '&:hover': {
              bgcolor: 'var(--color-primary-hover)',
            },
            width: 48,
            height: 48,
          }}
        >
          <CheckIcon />
        </IconButton>
      </DialogActions>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
